import { forwardRef, InputHTMLAttributes, ReactNode, useRef } from 'react';

import { inputRecipe } from './Input.css';
import { Error } from './InputError';
import { Hint } from './InputHint';
import { Label } from './InputLabel';
import { useInputIds } from '../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { mergeRefs } from '../../util';
import { getClassNames } from '../../util/css';
import { Stack } from '../Stack/Stack';

/** Props necessary for every Input* component */
export interface InputBaseProps {
  label: string;
  name: string;
  hint?: ReactNode;
  error?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
}

interface Props extends InputBaseProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'name'> {}

/** Shared input styles (Vanilla Extract recipe). Use for &lt;input&gt;, &lt;select&gt;, &lt;textarea&gt;. */
export { inputRecipe } from './Input.css';

export type { InputRecipeVariants } from './Input.css';

/** Styled input element. Use when you need the base Input look (e.g. InputNumber, InputStepper). */
export const StyledInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { isInvalid?: boolean; className?: string }
>(({ className, isInvalid, ...rest }, ref) => (
  <input
    ref={ref}
    className={getClassNames(inputRecipe({ isInvalid: !!isInvalid }), className)}
    {...rest}
  />
));

StyledInput.displayName = 'StyledInput';

/** Internal use only (InputText, InputTelephone, etc.) */
export const Input = forwardRef<HTMLInputElement, Props>(
  ({ error, hint, isOptional, label, name, type, ...rest }, ref) => {
    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });

    const localRef = useRef<HTMLInputElement>(null);
    const mergedRef = mergeRefs([ref, localRef]);

    useValidationErrorEvent({ error, name }, localRef);

    return (
      <div>
        <Stack gap="2">
          <Label htmlFor={inputId} isOptional={isOptional}>
            {label}
          </Label>
          <Stack.Item grow>
            <StyledInput
              aria-errormessage={errorId}
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
              id={inputId}
              isInvalid={!!error}
              name={name}
              ref={mergedRef}
              type={type}
              {...rest}
            />
            {error ? <Error id={errorId}>{error}</Error> : null}
          </Stack.Item>
          {hint ? <Hint id={hintId}>{hint}</Hint> : null}
        </Stack>
      </div>
    );
  },
);

Input.displayName = 'Input';
